import type { FC } from 'react';
import { Text } from '@launchpad-ui/components';

import { vars } from '../../../../../css/vars';
import type { BaseContentProps } from '../Content';

export interface NumberContentProps extends BaseContentProps {
  /**
   * The numeric value to display (number or string).
   * If not provided, displays "00".
   */
  value?: number | string;

  /**
   * Whether to disable truncation (truncation is enabled by default)
   * @default false
   */
  disableTruncation?: boolean;

  /**
   * Optional tooltip text to show on hover.
   * If not provided and truncation is enabled, the full number will be shown as a tooltip.
   */
  tooltip?: string;

  /**
   * Text style variant
   * @default 'default'
   */
  variant?: 'strong' | 'default' | 'small';
}

/**
 * Number content component for use inside Cell.
 *
 * @example
 * ```tsx
 * // Regular number
 * <Cell kind="regular" size="default">
 *   <NumberContent value={1234} />
 * </Cell>
 *
 * // String value (useful for formatted numbers like durations)
 * <Cell kind="regular" size="default">
 *   <NumberContent value="1.5ms" />
 * </Cell>
 *
 * // Strong number
 * <Cell kind="regular" size="default">
 *   <NumberContent value={42} variant="strong" />
 * </Cell>
 *
 * // Small number
 * <Cell kind="regular" size="default">
 *   <NumberContent value={99} variant="small" />
 * </Cell>
 *
 * // Number without truncation
 * <Cell kind="regular" size="default">
 *   <NumberContent value={123456789} disableTruncation />
 * </Cell>
 *
 * // Empty state
 * <Cell kind="regular" size="default">
 *   <NumberContent />
 * </Cell>
 * ```
 */
export const NumberContent: FC<NumberContentProps> = ({
  value,
  disableTruncation = false,
  tooltip,
  className,
  variant = 'default',
}) => {
  const getTextProps = () => {
    switch (variant) {
      case 'strong':
        return { size: 'medium' as const, bold: true };
      case 'small':
        return { size: 'small' as const, bold: false };
      case 'default':
      default:
        return { size: 'medium' as const, bold: false };
    }
  };

  const { size, bold } = getTextProps();
  const displayValue = value != null ? value.toString() : '00';
  const isEmpty = value == null;
  const shouldTruncate = !disableTruncation;

  return (
    <Text
      size={size}
      bold={bold}
      elementType="span"
      className={className}
      maxLines={shouldTruncate ? 1 : undefined}
      style={{
        fontFamily: 'var(--lp-font-family-monospace)',
        whiteSpace: disableTruncation ? 'normal' : 'nowrap',
        color: isEmpty ? vars.theme.interactive.fill.secondary.content.onDisabled : undefined,
      }}
      title={tooltip || (shouldTruncate ? displayValue : undefined)}
    >
      {displayValue}
    </Text>
  );
};
